import { ColorScheme, Spinner } from 'app/components/common/Spinner';
import ConditionalRedirect from 'app/components/router/ConditionalRedirect';
import { GetHistory } from 'app/components/router/History';
import { LoginRoute } from 'app/constants/Routes';
import IRoute from 'app/constants/Routes/IRoute';
import { isAuthenticatedSelector } from 'app/models/auth/selectors';
import { History } from 'app/services/history';
import { DMMAuthService, DMMLoginState } from 'app/services/oauth/DMMAuthService';
import { AppState } from 'app/store';
import { connectRenderProp } from 'app/utils/connect';
import { useCss } from 'app/utils/css';
import React from 'react';
import { connect } from 'react-redux';
import { BrandConsumer, BrandContextValue } from 'app/components/OrganizationContext';

interface IProps {
  isAuthenticated: boolean;
  currentBrand: BrandContextValue;
  history: History;
  excludeRoutes: IRoute[];
  children: React.ReactNode;
}

interface IState {
  loginState?: DMMLoginState;
}

export class DmmLoginRedirect extends React.Component<IProps, IState> {
  public state = {
    loginState: undefined,
  };

  private dmmAuthService: DMMAuthService;
  private mounted: boolean = false;

  public constructor(props: IProps) {
    super(props);
    this.dmmAuthService = new DMMAuthService(props.history);
  }

  public componentDidMount() {
    this.mounted = true;
    this.getDMMLoginState();
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public render() {
    // This component relies on the firstPartyDmm flag because this behavior is very specific to
    // the DMM brand. To remove the use of the DMM flag, a custom logout flow would need to be
    // encoded into the brand record. Since only DMM needs this at present, the flag is used instead.
    const { excludeRoutes, isAuthenticated, currentBrand, children } = this.props;
    const query = { automatic: true };
    // Will redirect to LoginRoute. The login page will correctly auto login dmm users.
    return currentBrand.firstPartyDmm && this.state.loginState === undefined ?
      (
        <CenteredSpinner />
      ) : (
        <ConditionalRedirect
          when={
            !isAuthenticated &&
            currentBrand.firstPartyDmm &&
            this.state.loginState === DMMLoginState.HAS_REGISTERED_DMM_LOGIN
          }
          to={LoginRoute.getPathname()}
          query={query}
          excludeRoutes={excludeRoutes}
        >
          {children}
        </ConditionalRedirect>
      );
  }

  private async getDMMLoginState() {
    try {
      const dmmLoginState = await this.dmmAuthService.fetchDMMLoginState();
      if (this.mounted) {
        this.setState({ loginState: dmmLoginState });
      }
    } catch (error) {
      if (this.mounted) {
        this.setState({ loginState: DMMLoginState.NO_DMM_LOGIN });
      }
    }
  }
}

const CenteredSpinner = () => {
  const css = useCss();
  return (
    <div
      data-testid="spinner"
      className={css('spinner', {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <Spinner color={ColorScheme.Dark} />
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const isAuthenticated = isAuthenticatedSelector(state);

  return {
    isAuthenticated,
  };
};

const DmmLoginRedirectInjectDependencies = connectRenderProp('currentBrand', BrandConsumer)(
  connectRenderProp('history', GetHistory)(
    connect(mapStateToProps)(DmmLoginRedirect),
  ),
);

export default DmmLoginRedirectInjectDependencies;
